import {
  Body,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalHeader,
  Shape,
  TabItem,
  Tabs,
  styled,
} from '@teko/ds-desktop';
import { AttributeGroup } from '@teko/ecommerce';
import { useProductDetailState } from 'contexts';
import DOMPurify from 'dompurify';
import { t } from 'i18n';
import { useRef, useState } from 'react';

interface SpecificationsProps {
  attributes: AttributeGroup[];
}

const MAX_PRODUCT_DESCRIPTION_HEIGHT = 503;

export const ProductDescription: React.FC = () => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const { product } = useProductDetailState();
  const displayAttributes = (
    product?.attributeGroups as SpecificationsProps['attributes']
  )?.filter((att) => !!att.value && att.parentId !== 0);

  const [isShowMoreAttributes, setShowMoreAttributes] = useState(false);
  const description = product?.seoInfo?.description;
  const [showMoreDescriptionNeeded, setShowMoreDescriptionNeeded] =
    useState(false);
  const [isExpanded, setExpanded] = useState(false);

  return (
    <WrapperProductDescription
      backgroundColor="white"
      borderRadius="xl"
      padding={['s0', 's16']}
    >
      <StyledTabs
        defaultTab={0}
        type="line"
        textActiveColor="black"
        tabTitleProps={{ style: { padding: 's16' } }}
        width="100%"
      >
        <TabItem
          minWidth="50%"
          className="tab-item"
          text={t('Thông tin sản phẩm')}
        >
          <SpecificationsWrapper>
            {!!displayAttributes?.length ? (
              <>
                <Specifications attributes={displayAttributes.slice(0, 8)} />
                {!!displayAttributes?.length && displayAttributes.length > 8 && (
                  <div onClick={() => setShowMoreAttributes(true)}>
                    <ShowMoreAttributesText
                      level={1}
                      weight="regular"
                      color="primary500"
                    >
                      {t('Xem thêm')}
                    </ShowMoreAttributesText>
                  </div>
                )}
              </>
            ) : (
              <Shape
                height={MAX_PRODUCT_DESCRIPTION_HEIGHT}
                backgroundColor="white"
              ></Shape>
            )}
          </SpecificationsWrapper>
        </TabItem>
        <TabItem minWidth="50%" className="tab-item" text={t('Mô tả chi tiết')}>
          {!!description ? (
            <Content>
              <Shape
                style={
                  showMoreDescriptionNeeded
                    ? {
                        overflow: 'hidden',
                        transition: 'height 0.3s ease',
                        height: isExpanded
                          ? contentRef.current?.clientHeight
                          : `${MAX_PRODUCT_DESCRIPTION_HEIGHT}px`,
                      }
                    : {}
                }
              >
                <StyledDescription
                  className="ck-content"
                  ref={(e) => {
                    !!e &&
                      !showMoreDescriptionNeeded &&
                      setShowMoreDescriptionNeeded(
                        e.clientHeight > MAX_PRODUCT_DESCRIPTION_HEIGHT,
                      );
                    contentRef.current = e;
                  }}
                  isExpanded={isExpanded}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify?.sanitize?.(description, {
                      ADD_TAGS: ['iframe'],
                    }),
                  }}
                  showMoreNeeded={showMoreDescriptionNeeded}
                />
              </Shape>
              {showMoreDescriptionNeeded ? (
                <Flex justify="center" padding="s12">
                  <StyledButton
                    variant="link"
                    onClick={() => setExpanded((previous) => !previous)}
                  >
                    {isExpanded ? t('Thu gọn') : t('Xem thêm')}
                  </StyledButton>
                </Flex>
              ) : null}
            </Content>
          ) : (
            <Shape
              height={MAX_PRODUCT_DESCRIPTION_HEIGHT}
              backgroundColor="white"
            ></Shape>
          )}
        </TabItem>
      </StyledTabs>
      <Modal
        show={isShowMoreAttributes}
        onHide={() => setShowMoreAttributes(false)}
      >
        <ModalHeader>{t('Thông tin sản phẩm')}</ModalHeader>
        <StyledModalBody>
          <Specifications attributes={displayAttributes} />
        </StyledModalBody>
      </Modal>
    </WrapperProductDescription>
  );
};

const WrapperProductDescription = styled(Shape)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 503px;
`;

const Specifications: React.FC<SpecificationsProps> = ({
  attributes,
}: SpecificationsProps) => {
  return (
    <Shape width="100%">
      {attributes.map((att, index) => {
        return (
          <AttributeWrapper
            key={`${index}${att.name}`}
            padding={['s12', 's16']}
            gap="s8"
          >
            <Body level={1} weight="regular" style={{ flex: 2 }}>
              {att.name}
            </Body>
            <Body
              level={1}
              weight="regular"
              dangerouslySetInnerHTML={{
                __html: DOMPurify?.sanitize?.(att.value, {
                  ADD_TAGS: ['iframe'],
                }),
              }}
              style={{ flex: 3 }}
            ></Body>
          </AttributeWrapper>
        );
      })}
    </Shape>
  );
};

const AttributeWrapper = styled(Flex)`
  &:nth-of-type(even) {
    background-color: #f6f6f6;
  }
`;

const SpecificationsWrapper = styled(Flex)`
  > * {
    max-width: 800px;
  }
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const ShowMoreAttributesText = styled(Body)`
  cursor: pointer;
  text-align: center;
  padding: 1.5rem;
`;

const StyledModalBody = styled(ModalBody)`
  max-height: 85vh;
  overflow-y: scroll;
  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 5px grey;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background: #cccccc;
    border-radius: 10px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #c8c5c5;
  }
`;

const StyledDescription = styled.div<{
  isExpanded: boolean;
  showMoreNeeded: boolean;
}>(
  ({
    isExpanded,
    showMoreNeeded,
  }: {
    isExpanded: boolean;
    showMoreNeeded: boolean;
  }) => `
  overflow: hidden;
  * {
    max-width: 100%;
  }
    
  .table table {
    table-layout: auto;
  }
  ul,
  ul li {
    list-style-type: inherit;
  }

  ul {
    /* Default user agent stylesheet. */
    padding-left: 40px;
  }

  ol,
  ol li {
    list-style-type: decimal;
  }
  ol {
    /* Default user agent stylesheet, you can change it to your needs. */
    padding-left: 40px;
  }
  ${
    !isExpanded && showMoreNeeded
      ? `
  &:after {
    content: ' ';
    background: linear-gradient(transparent, hsla(0, 0%, 100%, 0) 70%, #fff 90%);
    background-image: linear-gradient(rgba(255, 255, 255, 0), rgb(255, 255, 255));
    position: absolute;
    left: 0;
    bottom: 56px;
    height: 40px;
    width: 100%;
  };
  padding-bottom: 14px;
  `
      : ''
  }
`,
);

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const Content = styled.div`
  line-height: 1.5rem;
  padding: 16px;
  h2 {
    font-size: 20px;
    line-height: 1.4;
  }
  h3 {
    font-size: 16px;
    line-height: 1.3;
  }
  h4 {
    font-size: 14px;
  }
  h5 {
    font-size: 12px;
  }
  h6 {
    font-size: 10px;
  }
  img {
    height: auto;
  }

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StyledTabs = styled(Tabs)`
  .tab-item {
    padding: 16px;
  }
  .tab-item > div:is([type='body'], [type='subtitle']) {
    font-size: 18px;
  }
`;
